import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Markdown } from "../components/Markdown";
import { useApp } from "../context";
import { LEGAL_DOCS, legalText, parseLicenses, type LegalDoc } from "../legal";

/** Legal information: terms, data processing, privacy, sub-processors, imprint and open-source licences. */
export function Legal({ openId }: { openId?: string }) {
  const { t, i18n } = useTranslation();
  const { go } = useApp();
  const current = (LEGAL_DOCS as readonly string[]).includes(openId ?? "") || openId === "licenses" ? openId : undefined;

  return (
    <section className="narrow legal">
      <h1>{t("legal.page.title")}</h1>
      <p className="muted">{t("legal.page.intro")}</p>
      <nav className="legal-links" aria-label={t("legal.page.title")}>
        {[...LEGAL_DOCS, "licenses" as const].map((d) => (
          <a
            key={d}
            href={`#legal/${d}`}
            className={d === current ? "active" : undefined}
            aria-current={d === current ? "page" : undefined}
            onClick={(e) => {
              e.preventDefault();
              go("legal", d);
            }}
          >
            {t(`legal.doc.${d}`)}
          </a>
        ))}
      </nav>
      {current === "licenses" ? <Licenses /> : current ? <LegalDocument doc={current as LegalDoc} language={i18n.language} /> : null}
    </section>
  );
}

export function LegalDocument({ doc, language }: { doc: LegalDoc; language: string }) {
  const { t } = useTranslation();
  const { markdown, translated, language: shown } = legalText(doc, language);
  return (
    <article className="legal-doc">
      {translated && <p className="note">{t("legal.page.englishOnly")}</p>}
      <Markdown text={markdown} lang={shown} />
    </article>
  );
}

function Licenses() {
  const { t } = useTranslation();
  const rows = useMemo(() => parseLicenses(), []);
  return (
    <article className="legal-doc">
      <h2>{t("legal.doc.licenses")}</h2>
      <p className="muted">{t("legal.licenses.intro")}</p>
      <div className="table-scroll">
        <table className="list">
          <thead>
            <tr>
              <th>{t("legal.licenses.package")}</th>
              <th>{t("legal.licenses.version")}</th>
              <th>{t("legal.licenses.license")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <td>{r.homepage ? <a href={r.homepage} target="_blank" rel="noreferrer">{r.name}</a> : r.name}</td>
                <td>{r.version}</td>
                <td>{r.license}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}
